import { AsyncPipe } from '@angular/common';
import { Component, ContentChild, DestroyRef, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { OnboardingFacade } from '@zero/onboarding/domain';
import { ButtonComponent, InputComponent } from '@zero/shared/ui-web-design-system';
import { AuthStateFacade } from '@zero/shared/util-global-state';
import { PromotionService, RouteOptionsService, TrackingService } from '@zero/shared/util-web-app-services';
import { commonRoutes } from 'constants/web-app-constants';
import { BehaviorSubject, combineLatest, filter, firstValueFrom, map } from 'rxjs';
import { ExitIntentComponent } from '../../components/exit-intent/exit-intent.component';
import { FooterComponent } from '../../components/footer/footer.component';
import { EmailPipe } from '../../pipes/validation/email/email.pipe';

@Component({
  imports: [FooterComponent, ButtonComponent, TranslocoPipe, InputComponent, RouterLink, EmailPipe, AsyncPipe, ExitIntentComponent],
  templateUrl: './sign-up.component.html',
  styleUrl: './sign-up.component.scss',
})
export class SignUpComponent implements OnInit, OnDestroy {
  public readonly facade = inject(OnboardingFacade);

  readonly #routeOptionsService = inject(RouteOptionsService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #promotionService = inject(PromotionService);
  readonly #trackingService = inject(TrackingService);
  readonly #authStateFacade = inject(AuthStateFacade);
  @ContentChild(ButtonComponent) public btn?: ButtonComponent;

  readonly #email = new BehaviorSubject<string>('');

  public readonly loginPage = commonRoutes.prelogin.home;

  public readonly email$ = this.#email.asObservable();
  public showPromotionInfo = false;

  public readonly isFormValid$ = combineLatest([this.email$, this.facade.sendingEmail$]).pipe(
    map(([email, sending]) => Boolean(new EmailPipe().transform(email) && !sending)),
  );

  public readonly isCustomer$ = this.#authStateFacade.customerId$.pipe(map((customerId) => !!customerId));

  ngOnInit(): void {
    this.#routeOptionsService.hideHeaderAndFooter();
    this.#routeOptionsService.toggleGradient();
    this.#listenToEmailSent();
    this.showPromotionInfo = !!this.#promotionService.getAccountOpenReferenceId()?.startsWith('KWK-');
    this.#trackingService.trackEvent('reg_startconf', 'register', 'component');
  }

  ngOnDestroy(): void {
    this.#routeOptionsService.displayHeaderAndFooter();
    this.#routeOptionsService.toggleGradient();
  }

  @HostListener('window:keydown', ['$event'])
  public async pressEnter(event: KeyboardEvent): Promise<void> {
    event.key === 'Enter' && (await firstValueFrom(this.isFormValid$)) && this.submit();
  }

  public handleEmailChange = (email: string) => this.#email.next(email.toLowerCase());

  public submit = () => this.facade.sendEmail(this.#email.value);

  #listenToEmailSent(): void {
    this.facade.sentEmailSuccessfully$
      .pipe(
        filter((sent) => !!sent),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.facade.goToNextStep());
  }
}
